import { useMemo, useState } from "react";
import { Download, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { loadRequests, removeRequestItem, updateRequest, updateRequestItem } from "../lib/storage";
import { canApprove, canConfirmDelivery, isMain } from "../lib/permissions";
import { buildCsvFilename, downloadCsv, requestsToCsvRows } from "../lib/csv";
import { buildPdfFilename, downloadRequestsPdf } from "../lib/pdf";
import { useRequestFilters } from "../hooks/useRequestFilters";
import RequestFilters from "./requests/RequestFilters";
import PurchaseGroups from "./requests/PurchaseGroups";
import TransferList from "./requests/TransferList";
import DeliveryConfirmDialog from "./delivery/DeliveryConfirmDialog";
import PendingDirectPurchasePanel from "./delivery/PendingDirectPurchasePanel";

// Thin orchestrator:
//  1. Owns filter state (via hook)
//  2. Dispatches approve/reject/delete actions
//  3. Delegates rendering to purchase/transfer sub-components
export default function RequestsPanel({
  user,
  requests,
  products,
  suppliers,
  onRequestsChange,
  scope = "all", // all | purchase | transfer
}) {
  const { state, setters, filtered } = useRequestFilters(requests, scope);

  const transfers = useMemo(
    () => (scope === "purchase" ? [] : filtered.filter((r) => r.type === "internal_transfer")),
    [filtered, scope]
  );

  const approveRequest = (r) => {
    if (!canApprove(user, r)) return toast.error("Not allowed");
    onRequestsChange(
      updateRequest(r.id, {
        status: "approved",
        approvedBy: user.id,
        approvedAt: new Date().toISOString(),
      })
    );
  };
  const rejectRequest = (r) => {
    if (!canApprove(user, r)) return toast.error("Not allowed");
    onRequestsChange(
      updateRequest(r.id, {
        status: "rejected",
        approvedBy: user.id,
        approvedAt: new Date().toISOString(),
      })
    );
  };
  const fulfillTransfer = (r) => {
    if (!canApprove(user, r)) return toast.error("Not allowed");
    onRequestsChange(updateRequest(r.id, { status: "completed" }));
  };

  const updateLine = (request, lineKey, patch, message) => {
    if (!canApprove(user, request) && !canConfirmDelivery(user, request)) {
      return toast.error("Not allowed");
    }
    onRequestsChange(updateRequestItem(request.id, lineKey, patch));
    if (message) toast.success(message);
  };

  const approveLine = (request, lineKey) => {
    const nowIso = new Date().toISOString();
    updateLine(request, lineKey, {
      status: "approved",
      approvedBy: user.id,
      approvedAt: nowIso,
    }, "Item approved");
  };

  const rejectLine = (request, lineKey) => {
    const nowIso = new Date().toISOString();
    updateLine(request, lineKey, {
      status: "rejected",
      approvedBy: user.id,
      approvedAt: nowIso,
      purchased: false,
    }, "Item rejected");
  };

  const markSupplierUnavailable = (request, lineKey) => {
    const nowIso = new Date().toISOString();
    updateLine(request, lineKey, {
      status: "supplier_unavailable",
      unavailableBy: user.id,
      unavailableByName: user.name,
      unavailableAt: nowIso,
      purchased: false,
    }, "Item marked unavailable at supplier");
  };

  const deleteLine = (request, lineKey) => {
    if (!isMain(user)) return toast.error("Not allowed");
    onRequestsChange(removeRequestItem(request.id, lineKey));
    toast("Item removed");
  };

  const toggleLinePurchased = (request, lineKey, purchased) => {
    const nowIso = new Date().toISOString();
    const line = (request.items || []).find((item, index) =>
      (item.itemId || `${item.productId || item.productName || "item"}::${index}`) === lineKey
    );
    updateLine(request, lineKey, {
      status: purchased ? "completed" : "approved",
      purchased,
      purchasedQty: purchased ? Number(line?.quantity ?? line?.qty ?? 0) : 0,
      purchasedBy: purchased ? user.id : null,
      purchasedByName: purchased ? user.name : null,
      purchasedAt: purchased ? nowIso : null,
      purchasedSupplier: purchased ? request.vendor : null,
    }, purchased ? "Item marked purchased" : "Item marked pending");
  };

  const approveVisibleGroup = (group) => {
    const nowIso = new Date().toISOString();
    let next = loadRequests();
    group.requests.forEach((request) => {
      if (!canApprove(user, request)) return;
      const items = (request.items || []).map((line) => {
        const status = line.status || (line.purchased ? "completed" : request.status);
        if (status !== "submitted" && status !== "pending") return line;
        return {
          ...line,
          status: "approved",
          approvedBy: user.id,
          approvedAt: nowIso,
        };
      });
      next = updateRequest(request.id, {
        items,
        status: items.every((line) => line.status === "approved") ? "approved" : request.status,
      });
    });
    onRequestsChange(next);
    toast.success("Visible group approved");
  };

  const exportCsv = () => {
    if (filtered.length === 0) {
      return toast.error("Nothing to export with current filters");
    }
    const rows = requestsToCsvRows(filtered, products, suppliers);
    downloadCsv(buildCsvFilename(scope), rows);
    toast.success(`Exported ${filtered.length} request${filtered.length === 1 ? "" : "s"}`);
  };

  const exportPdf = async () => {
    if (filtered.length === 0) {
      return toast.error("Nothing to export with current filters");
    }
    const created = await downloadRequestsPdf(buildPdfFilename(), filtered, products, suppliers);
    if (!created) return toast.error("No active purchase items match the current filters");
    toast.success("Purchase request PDF created");
  };

  // ---------- Delivery confirmation ----------
  // `dialog.requests` holds the list of requests being confirmed in one shot.
  // `dialog.supplier` is the vendor we're confirming against (for the header).
  const [dialog, setDialog] = useState({ open: false, requests: [], supplier: null });

  const openGroupConfirm = (group, approvedAwaiting) => {
    if (!approvedAwaiting.length) return toast.error("Nothing to confirm in this group");
    setDialog({ open: true, requests: approvedAwaiting, supplier: group.supplier });
  };

  const openLineConfirm = (r) => {
    const supplier = suppliers.find((s) => s.id === r.vendor) || null;
    setDialog({ open: true, requests: [r], supplier });
  };

  const onConfirmed = () => {
    // storage has been updated inside the dialog; re-read from localStorage
    // so every memoized view refreshes.
    onRequestsChange(loadRequests());
  };

  const productById = useMemo(() => {
    const m = new Map((products || []).map((p) => [p.id, p]));
    return (id) => m.get(id);
  }, [products]);

  return (
    <div data-testid="requests-panel">
      {isMain(user) && (
        <div className="flex flex-wrap justify-end gap-2 mb-2">
          <Button
            data-testid="export-csv-btn"
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={filtered.length === 0}
            className="border-[#E6E0D4] bg-white hover:bg-[#F3EFE7] text-[#0B1F3A]"
          >
            <Download size={14} className="mr-1.5" /> Export CSV
            <span className="ml-1.5 text-[#0B1F3A]/50 text-xs">({filtered.length})</span>
          </Button>
          <Button
            data-testid="export-pdf-btn"
            variant="outline"
            size="sm"
            onClick={exportPdf}
            disabled={filtered.length === 0}
            className="border-[#E6E0D4] bg-white hover:bg-[#F3EFE7] text-[#0B1F3A]"
          >
            <FileDown size={14} className="mr-1.5" /> Export PDF
            <span className="ml-1.5 text-[#0B1F3A]/50 text-xs">({filtered.length})</span>
          </Button>
        </div>
      )}
      <RequestFilters
        user={user}
        scope={scope}
        state={state}
        setters={setters}
        products={products}
        suppliers={suppliers}
      />

      {filtered.length === 0 && (
        <div className="bg-white border border-[#E6E0D4] rounded-xl p-10 text-center" data-testid="requests-empty">
          <p className="text-[#0B1F3A]/60">No requests match these filters.</p>
        </div>
      )}

      {scope !== "transfer" && (
        <PurchaseGroups
          filtered={filtered}
          suppliers={suppliers}
          products={products}
          user={user}
          onApprove={approveRequest}
          onReject={rejectRequest}
          onApproveLine={approveLine}
          onRejectLine={rejectLine}
          onUnavailableLine={markSupplierUnavailable}
          onApproveAll={approveVisibleGroup}
          onDeleteLine={deleteLine}
          onConfirmGroup={openGroupConfirm}
          onConfirmLine={openLineConfirm}
          onToggleLinePurchased={toggleLinePurchased}
        />
      )}

      {scope !== "transfer" && (
        <PendingDirectPurchasePanel
          requests={requests}
          products={products}
          suppliers={suppliers}
          user={user}
          onChanged={() => onRequestsChange(loadRequests())}
        />
      )}

      {scope !== "purchase" && (
        <TransferList
          transfers={transfers}
          products={products}
          user={user}
          onApprove={approveRequest}
          onReject={rejectRequest}
          onFulfill={fulfillTransfer}
        />
      )}

      <DeliveryConfirmDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        requests={dialog.requests}
        supplier={dialog.supplier}
        productById={productById}
        user={user}
        onConfirmed={onConfirmed}
      />
    </div>
  );
}
